import { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { visuallyHidden } from '@mui/utils';
import Typography from '@mui/material/Typography';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { CriteriaData, CriteriaPanelProps, CriteriaState } from './types';
import { generateDefaultState, mergeWithParsedConfig, transformStateForBackend } from './stateSchema';
import { CriteriaTabPane } from './Panes';
import { SummaryPane } from './SummaryPane';
import { CriteriaSearch, type SearchAction } from './CriteriaSearch';
import {
  availableSections,
  availableTabs,
  CRITERIA_SECTIONS,
  isSectionConfigured,
  type CriteriaTabKey,
} from './sections';
import { useSectionContext } from './sections/context';
import { validateCriteriaState } from './validation';
import { ruleBuilderSx } from './ruleBuilderStyles';
// Side-effect import: nothing in here is a class any more — what is left are the :global blocks
// that style reactstrap inputs. The rule builders are styled by ruleBuilderSx on the card.
import './CriteriaPanel.module.css';

const CARD_BODY_BG =
  'color-mix(in srgb, var(--mui-palette-background-default) 50%, var(--mui-palette-background-paper))';

/** How long the card takes to open or close. */
const COLLAPSE_MS = 300;

export function CriteriaPanel({
  sourceType = 'default',
  criteria,
  onChange,
  showAllErrors = false,
  hideCapsTab = false,
}: CriteriaPanelProps) {
  const context = useSectionContext(sourceType, hideCapsTab);
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState<'summary' | CriteriaTabKey>('summary');
  // Criteria search: sections pinned to the Summary, and the one just jumped to.
  const [pinnedIds, setPinnedIds] = useState<ReadonlySet<string>>(() => new Set());
  // `delay` holds the scroll back until a collapsed card has finished opening.
  const [focus, setFocus] = useState<{ id: string; delay: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Once the tab holding the picked section has rendered, bring the section into view and
  // ring it for a moment.
  useEffect(() => {
    if (!focus) return;
    const scroll = setTimeout(() => {
      const target = rootRef.current?.querySelector<HTMLElement>(`[data-criteria-section="${focus.id}"]`);
      // Centred, so a page's own sticky header can't cover it; a section too tall to centre
      // is aligned to the top instead so its heading stays in view.
      const fitsCentred = (target?.offsetHeight ?? 0) < window.innerHeight * 0.6;
      target?.scrollIntoView?.({ behavior: 'smooth', block: fitsCentred ? 'center' : 'start' });
    }, focus.delay);
    const clear = setTimeout(() => setFocus(null), focus.delay + 1600);
    return () => {
      clearTimeout(scroll);
      clearTimeout(clear);
    };
  }, [focus]);

  const handleSearchPick = (sectionId: string, action: SearchAction) => {
    const section = CRITERIA_SECTIONS.find((s) => s.id === sectionId);
    if (!section) return;
    const opening = !isExpanded;
    setIsExpanded(true);
    if (action === 'pin') {
      setPinnedIds((prev) => new Set(prev).add(sectionId));
      setActiveTab('summary');
    } else {
      setActiveTab(section.tab);
    }
    setFocus({ id: sectionId, delay: opening ? COLLAPSE_MS + 50 : 0 });
  };
  const getInitialState = (): CriteriaState => {
    // Initialize from criteria if available
    if (criteria?.criteriaConfiguration) {
      try {
        const parsedConfig = JSON.parse(criteria.criteriaConfiguration);
        return mergeWithParsedConfig(parsedConfig);
      } catch (error) {
        console.warn('Failed to parse criteria configuration:', error);
      }
    }

    return generateDefaultState();
  };

  const initialStateRef = useRef<CriteriaState | null>(null);
  if (initialStateRef.current === null) {
    initialStateRef.current = getInitialState();
  }

  const [state, setState] = useState<CriteriaState>(initialStateRef.current as CriteriaState);
  const [validationResult, setValidationResult] = useState(() => validateCriteriaState(initialStateRef.current!));
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const isInternalUpdateRef = useRef(false);
  // The state is derived from the configuration string alone, so that is what counts as a change:
  // a caller handing over a new object with the same configuration must not reset the panel.
  const lastConfigurationRef = useRef(criteria?.criteriaConfiguration);

  // Update state when criteria prop changes (but not from our own updates)
  useEffect(() => {
    // Skip if this update came from our own handleStateChange
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    // Only update if criteria actually changed externally
    if (criteria?.criteriaConfiguration !== lastConfigurationRef.current) {
      const newState = getInitialState();
      initialStateRef.current = newState;
      setState(newState);
      setValidationResult(validateCriteriaState(newState));
      lastConfigurationRef.current = criteria?.criteriaConfiguration;
    }
  }, [criteria]);

  // The latest state, including updates not yet rendered — so two updates in one event compose
  // instead of the second one starting from the state the first replaced.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Handle state changes and notify parent
  const handleStateChange = (newState: React.SetStateAction<CriteriaState>) => {
    const updatedState = typeof newState === 'function' ? newState(stateRef.current) : newState;
    stateRef.current = updatedState;
    setState(updatedState);
    const validation = validateCriteriaState(updatedState);
    setValidationResult(validation);

    if (onChange) {
      // Sanitize state: clear dependent fields when their parent selector is empty
      const sanitizedState = { ...updatedState };

      // UsersPane - Device & Geo fields
      if (!sanitizedState.devicesIncludeExclude) {
        sanitizedState.devices = [];
      }
      if (!sanitizedState.browsersIncludeExclude) {
        sanitizedState.browsers = [];
      }
      if (!sanitizedState.domainsIncludeExclude) {
        sanitizedState.domains = [];
      }
      if (!sanitizedState.operatingSystemIncludeExclude) {
        sanitizedState.operatingSystems = [];
      }
      if (!sanitizedState.statesIncludeExclude) {
        sanitizedState.statesValues = [];
      }
      if (!sanitizedState.zipsIncludeExclude) {
        sanitizedState.zipsValues = '';
      }
      if (!sanitizedState.userIdLastDigitIncludeExclude) {
        sanitizedState.userIdLastDigitValues = [];
      }

      // Users Pane - Lists & Segments
      if (!sanitizedState.aibListIncludeExclude) {
        sanitizedState.aibList = [];
      }
      if (!sanitizedState.requiredUserDataIncludeExclude) {
        sanitizedState.requiredUserDataValues = [];
      }

      // Pre-ping Pane - External Pre-ping fields
      if (!sanitizedState.prePingMethod) {
        sanitizedState.prePingHandlerId = null;
        sanitizedState.prePingUrl = '';
        sanitizedState.prePingBody = '';
        sanitizedState.prePingTimeout = undefined;
        sanitizedState.prePingHeader1 = '';
        sanitizedState.prePingHeader2 = '';
        sanitizedState.prePingHeader3 = '';
        sanitizedState.prePingCustomHttpStatus = '';
      }

      // Attribute Pane - User Attributes
      if (!sanitizedState.userAttributesIncludeExclude) {
        sanitizedState.userAttributes = null;
      }

      // Attribute Pane - URL Parameters
      if (!sanitizedState.urlParametersIncludeExclude) {
        sanitizedState.urlParameterRules = null;
      }

      // Attribute Pane - User Agent
      if (!sanitizedState.userAgentIncludeExclude) {
        sanitizedState.userAgentRules = null;
      }

      // Attribute Pane - Affiliate Controls
      if (!sanitizedState.affiliateNamesIncludeExclude) {
        sanitizedState.affiliateNames = [];
      }
      if (!sanitizedState.affSubIDConfiguration?.includeExclude) {
        sanitizedState.affSubIDConfiguration = {
          includeExclude: '',
          affiliate: '',
          subIds: '',
          subIdList: [],
          andOrLogic: 'AND',
        };
      }

      // Attribute Pane - Email Domains
      if (!sanitizedState.emailDomainsIncludeExclude) {
        sanitizedState.emailDomains = [];
      }

      // Attribute Pane - Landing Pages
      if (!sanitizedState.landingPageGroupIncludeExclude) {
        sanitizedState.landingPageGroupIds = [];
      }
      if (!sanitizedState.landingPageIncludeExclude) {
        sanitizedState.landingPageIds = [];
      }

      // Attribute Pane - Question/Answers
      if (!sanitizedState.question) {
        sanitizedState.questionAnswers = [];
      }

      // Pre-ping Pane - Internal Pre-pings: filter out incomplete entries
      // An entry is considered incomplete if type is not set
      if (sanitizedState.internalPrePings?.internalPrePings) {
        const validTypes = ['days', 'hours', 'minutes'];
        const filteredPrePings = sanitizedState.internalPrePings.internalPrePings.filter(
          (entry) => entry.prePingSuppressionType && validTypes.includes(entry.prePingSuppressionType.toLowerCase())
        );
        sanitizedState.internalPrePings = {
          ...sanitizedState.internalPrePings,
          internalPrePings: filteredPrePings,
        };
      }

      // Transform the state for backend before serialization
      const backendState = transformStateForBackend(sanitizedState);

      const criteriaData: CriteriaData = {
        criteriaConfiguration: JSON.stringify(backendState),
        tags: criteria?.tags || {},
        createdAt: criteria?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        validationErrors: validation.errors,
        validationSummary: validation.summary,
        isValid: !validation.hasErrors,
      };

      // Mark this as an internal update to prevent useEffect from overriding
      isInternalUpdateRef.current = true;

      onChange(criteriaData);
    }
  };

  // Stable identities for the callbacks every section receives, so a section memoised on its
  // own slice of state (see SectionBody) isn't re-rendered just because these were recreated.
  const handleStateChangeRef = useRef(handleStateChange);
  handleStateChangeRef.current = handleStateChange;
  const stableSetState = useCallback<React.Dispatch<React.SetStateAction<CriteriaState>>>(
    (next) => handleStateChangeRef.current(next),
    []
  );

  // Mark a field as touched
  const markFieldTouched = useCallback((fieldPath: string) => {
    setTouchedFields((prev) => (prev.has(fieldPath) ? prev : new Set(prev).add(fieldPath)));
  }, []);

  // Filter errors to only show for touched fields or after save attempt
  const filterErrors = (errors: Record<string, unknown> | undefined): Record<string, unknown> => {
    if (!errors || showAllErrors) return errors || {};

    const filtered: Record<string, unknown> = {};

    const checkPath = (obj: Record<string, unknown>, basePath: string = ''): Record<string, unknown> => {
      const result: Record<string, unknown> = {};

      Object.keys(obj).forEach((key) => {
        const currentPath = basePath ? `${basePath}.${key}` : key;
        const value = obj[key];

        // Check if this exact path is touched
        if (touchedFields.has(currentPath)) {
          result[key] = value;
        } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          // Recursively check nested objects
          const nestedResult = checkPath(value as Record<string, unknown>, currentPath);
          if (Object.keys(nestedResult).length > 0) {
            result[key] = nestedResult;
          }
        }
      });

      return result;
    };

    return checkPath(errors);
  };

  const tabs = availableTabs(context);
  // Fall back to the Summary if the active tab stops applying (a source type change, say).
  const currentTab = activeTab === 'summary' || tabs.some((tab) => tab.key === activeTab) ? activeTab : 'summary';

  // How many sections of each tab hold configuration — shown on the tab, so what is set up
  // reads at a glance without opening every tab.
  const configuredByTab = new Map<CriteriaTabKey, number>();
  for (const section of availableSections(context)) {
    if (isSectionConfigured(section, state, context)) {
      configuredByTab.set(section.tab, (configuredByTab.get(section.tab) ?? 0) + 1);
    }
  }
  const configuredTotal = Array.from(configuredByTab.values()).reduce((sum, n) => sum + n, 0);

  const paneProps = {
    state,
    setState: stableSetState,
    sourceType,
    errors: filterErrors(validationResult.errors),
    markFieldTouched,
  };

  return (
    // overflow stays visible so the card does not become the scroll root for the sticky header
    // below; pages that scroll this panel put the border and the radius on the wrapper.
    <Card ref={rootRef} sx={[{ overflow: 'visible' }, ruleBuilderSx]}>
      <CardHeader
        // Sticky so the title — and the control that collapses the card — stays reachable while a
        // long criteria form scrolls past. Inert wherever the card does not scroll.
        sx={{
          cursor: 'pointer',
          position: 'sticky',
          top: 0,
          zIndex: 2,
          // The theme's section band; restated here because sticky needs an opaque background
          // to cover the content scrolling underneath it.
          bgcolor: 'zuul.sectionHeader',
          borderTopLeftRadius: 'inherit',
          borderTopRightRadius: 'inherit',
        }}
        title={
          <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
            {/* h5 matches the 1rem/600 the theme gives every other section band title. */}
            <Typography variant='h5' component='h4'>
              Criteria
            </Typography>
            {configuredTotal > 0 && (
              <Chip size='small' color='primary' variant='outlined' label={`${configuredTotal} configured`} />
            )}
          </Stack>
        }
        slotProps={{ title: { component: 'div' }, action: { sx: { alignSelf: 'center', m: 0 } } }}
        action={
          // The header toggles the card on click; the search is a control of its own.
          <Box onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
            <CriteriaSearch
              context={context}
              defaultAction={currentTab === 'summary' ? 'pin' : 'open'}
              onPick={handleSearchPick}
            />
          </Box>
        }
      />
      {/* The body sits halfway between the page and the sections: dark enough that each white
          criteria section stands off it as a card of its own, light enough that the card still
          reads as a card on the page rather than a hole in it. Mixed from the theme's own two
          surfaces, so it follows every preset and dark mode. */}
      <CardContent
        sx={{ bgcolor: CARD_BODY_BG, borderBottomLeftRadius: 'inherit', borderBottomRightRadius: 'inherit' }}
      >
        <Collapse in={isExpanded} timeout={COLLAPSE_MS}>
          {/* MUI Tabs replace the hand-rolled ul/li/button list, so keyboard navigation and the
              active indicator come from the design system rather than from module CSS. */}
          <Tabs
            value={currentTab}
            onChange={(_event, next) => setActiveTab(next)}
            variant='scrollable'
            scrollButtons='auto'
            allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, minHeight: 40 }}
          >
            <Tab value='summary' label='Summary' sx={{ minHeight: 40 }} />
            {tabs.map((tab) => {
              const count = configuredByTab.get(tab.key) ?? 0;
              return (
                <Tab
                  key={tab.key}
                  value={tab.key}
                  sx={{ minHeight: 40 }}
                  label={
                    <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                      {tab.title}
                      {count > 0 && (
                        <Box
                          component='span'
                          aria-hidden
                          sx={{
                            minWidth: 18,
                            height: 18,
                            px: 0.5,
                            borderRadius: 9,
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            fontSize: '0.6875rem',
                            fontWeight: 600,
                            lineHeight: '18px',
                            textAlign: 'center',
                          }}
                        >
                          {count}
                        </Box>
                      )}
                      {count > 0 && (
                        <Box component='span' sx={visuallyHidden}>
                          {` (${count} configured)`}
                        </Box>
                      )}
                    </Box>
                  }
                />
              );
            })}
          </Tabs>

          {currentTab === 'summary' ? (
            <SummaryPane
              {...paneProps}
              context={context}
              onOpenTab={setActiveTab}
              pinnedIds={pinnedIds}
              onUnpin={(id) =>
                setPinnedIds((prev) => {
                  const next = new Set(prev);
                  next.delete(id);
                  return next;
                })
              }
              highlightId={focus?.id ?? null}
            />
          ) : (
            <CriteriaTabPane {...paneProps} tab={currentTab} context={context} highlightId={focus?.id ?? null} />
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
}
